import { useMemo, useState } from 'react';
import type { HolderHistoryPoint, MapData } from '@pleco/shared';
import { useMapStore } from '../store/mapStore.js';
import { IconDownload, IconLink } from './Icons.js';

/* ------------------------------------------------------------------ *
 * Toolbar
 * ------------------------------------------------------------------ */

export function MapToolbar({
  data,
  onExport,
  onShare,
}: {
  data: MapData;
  onExport: () => void;
  onFit?: () => void;
  onShare: () => void;
  onRefresh?: () => void;
}) {
  const { options, setOptions } = useMapStore();

  return (
    <div className="map-toolbar">
      <button className="tm-btn" onClick={onExport} title="Save the map as a PNG image">
        <IconDownload /> Download image
      </button>
      <button className="tm-btn" onClick={onShare} title="Copy a link to this map">
        <IconLink /> Copy link
      </button>
      {options.timestamp && (
        <span className="chip accent dot">
          Showing {new Date(options.timestamp * 1000).toLocaleString()}
          <button style={{ marginLeft: 6 }} onClick={() => setOptions({ timestamp: undefined })} aria-label="Back to now">
            ✕
          </button>
        </span>
      )}
      {data.partial && <span className="chip warn dot">updating…</span>}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Legend
 * ------------------------------------------------------------------ */

export function Legend({ data }: { data: MapData }) {
  const [open, setOpen] = useState(true);

  if (!open) {
    return (
      <button className="legend legend-collapsed" onClick={() => setOpen(true)} title="Show map key">
        ⓘ How to read the map
      </button>
    );
  }

  return (
    <div className="legend">
      <div className="legend-head">
        <span>Map key</span>
        <button onClick={() => setOpen(false)} title="Hide map key">
          ✕
        </button>
      </div>
      <div className="legend-row">
        <i className="legend-dot" style={{ background: 'var(--bubble-solo)' }} />
        Independent holder
      </div>
      {data.clusters.length > 0 && (
        <div className="legend-row" style={{ alignItems: 'flex-start' }}>
          <span className="legend-swatches">
            {data.clusters.slice(0, 6).map((c) => (
              <i key={c.id} className="legend-dot" style={{ background: c.color }} />
            ))}
          </span>
          <span>Same colour = same owners</span>
        </div>
      )}
      <div className="legend-row">
        <i className="legend-dot legend-dot-magic" />
        Funding wallet (holds none)
      </div>
      <div className="legend-row faint" style={{ marginTop: 5, fontSize: 11 }}>
        Bubble size = share of supply
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Time travel scrubber
 * ------------------------------------------------------------------ */

export function TimeTravel({ history, data }: { history: HolderHistoryPoint[]; data: MapData }) {
  const { options, setOptions } = useMapStore();

  const { min, max } = useMemo(() => {
    if (history.length > 0) {
      return { min: history[0]!.t, max: history[history.length - 1]!.t };
    }
    const created = data.token.createdAt ?? data.asOf - 86_400 * 30;
    return { min: created, max: Math.floor(Date.now() / 1000) };
  }, [data.asOf, data.token.createdAt, history]);

  const current = options.timestamp ?? max;
  const pct = max > min ? ((current - min) / (max - min)) * 100 : 100;
  const peak = Math.max(1, ...history.map((h) => h.holders));

  const jump = (clientX: number, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const t = Math.round(min + ratio * (max - min));
    setOptions({ timestamp: t >= max - 60 ? undefined : t });
  };

  return (
    <div className="timebar">
      <div className="timebar-label">
        {options.timestamp ? new Date(current * 1000).toLocaleString() : 'Live · now'}
      </div>

      <div
        className="timebar-track"
        onClick={(e) => jump(e.clientX, e.currentTarget)}
        title="Click to rewind the map to that moment"
      >
        {history.length === 0
          ? // Skeleton track: the scrubber still works, it just has no shape yet.
            Array.from({ length: 48 }, (_, i) => (
              <i key={i} className="timebar-bar skeleton" style={{ height: `${18 + (i % 7) * 4}%` }} />
            ))
          : history.map((h) => (
              <i
                key={h.t}
                className={`timebar-bar ${h.t <= current ? 'past' : ''}`}
                style={{ height: `${Math.max(6, (h.holders / peak) * 100)}%` }}
                title={`${new Date(h.t * 1000).toLocaleDateString()} · ${h.holders} holders · top10 ${h.top10}%`}
              />
            ))}
        <div className="timebar-cursor" style={{ left: `${pct}%` }} />
        <span className="timebar-axis left">{new Date(min * 1000).toLocaleDateString()}</span>
        <span className="timebar-axis right">
          {history.length > 0 ? `${peak} holders peak` : 'indexing…'}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 5 }}>
        <button
          className="btn sm"
          onClick={() => setOptions({ timestamp: min + 60 })}
          title="Jump to launch"
        >
          ⏮ Launch
        </button>
        <button
          className={`btn sm ${options.timestamp ? '' : 'active'}`}
          onClick={() => setOptions({ timestamp: undefined })}
        >
          Live
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Status strip
 * ------------------------------------------------------------------ */

export function StatusStrip({ data }: { data: MapData }) {
  const status = useMapStore((s) => s.status);
  const warnings = data.warnings ?? [];
  if (!data.partial && warnings.length === 0) return null;

  return (
    <>
      {data.partial && (
        <div className="statusbar">
          <span className="spinner" />
          <span>
            Indexing chain history
            {status ? ` · ${Math.round(status.progress * 100)}% · ${status.transfers.toLocaleString()} transfers` : ''}
          </span>
          <div className="progress">
            <i style={{ width: `${Math.round((status?.progress ?? 0) * 100)}%` }} />
          </div>
        </div>
      )}
      {warnings.map((w) => (
        <div className="statusbar warn" key={w}>
          <span>⚠</span>
          <span>{w}</span>
        </div>
      ))}
    </>
  );
}
